<template>
  <div :class="$style['filters']">
    <template v-for="group in groups" :key="group.type">
      <fieldset v-if="group.items.length" :class="$style['filters__group']">
        <legend :class="$style['filters__title']">{{ group.title }}</legend>
        <div :id="`filters-${group.type}`" :class="$style['filters__options']">
          <FormCheckbox
            v-for="item in visibleItems(group)"
            :key="item.slug"
            :label="item.title.trim()"
            :checked="filters[group.type].includes(item.slug)"
            @change="$emit('toggle-filter', { type: group.type, value: item.slug })">
            <span v-if="item.hex" :class="$style['filters__swatch']" :style="{ backgroundColor: item.hex }" aria-hidden="true" />
          </FormCheckbox>
        </div>
        <button
          v-if="!showAll && group.items.length > LIMIT"
          type="button"
          :class="$style['filters__more']"
          :aria-expanded="expanded.includes(group.type)"
          :aria-controls="`filters-${group.type}`"
          @click="toggleExpanded(group.type)">
          {{ expanded.includes(group.type) ? 'Toon minder' : `Toon alle ${group.items.length}` }}
        </button>
      </fieldset>
    </template>
  </div>
</template>

<script setup lang="ts">
// Checkbox groups for categories, colours and themes. Long groups show the first few and every chosen one.
interface FilterItem {
  id: number
  slug: string
  title: string
  hex?: string
}

type FilterType = 'categories' | 'colors' | 'themes'

interface Props {
  categories?: FilterItem[]
  colors: FilterItem[]
  themes: FilterItem[]
  filters: Record<FilterType, string[]>
  hideCategories?: boolean
  showAll?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hideCategories: false,
  showAll: false,
  categories: () => []
})

defineEmits<{
  (e: 'toggle-filter', payload: { type: FilterType, value: string }): void
}>()

const LIMIT = 8
const expanded = ref<FilterType[]>([])

const groups = computed(() => [
  ...(props.hideCategories ? [] : [{ type: 'categories' as const, title: 'Categorieën', items: props.categories }]),
  { type: 'colors' as const, title: 'Kleuren', items: props.colors },
  { type: 'themes' as const, title: "Thema's", items: props.themes }
])

function visibleItems (group: { type: FilterType, items: FilterItem[] }) {
  if (props.showAll || expanded.value.includes(group.type)) { return group.items }
  return group.items.filter((item, index) => index < LIMIT || props.filters[group.type].includes(item.slug))
}

function toggleExpanded (type: FilterType) {
  expanded.value = expanded.value.includes(type)
    ? expanded.value.filter(value => value !== type)
    : [...expanded.value, type]
}
</script>

<style module>
.filters {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxl);
}

.filters__group {
  margin: 0;
  padding: 0;
  border: 0;
}

/* A label above the options, like on the product page; the page title is the only big heading */
.filters__title {
  margin-bottom: var(--spacing-m);
  padding: 0;
  color: var(--text-subtle);
  font-size: var(--font-size-small);
}

.filters__options {
  display: flex;
  flex-direction: column;
  gap: var(--spacing);
}

.filters__swatch {
  flex: none;
  width: 1rem;
  height: 1rem;
  border: var(--stage-hairline) solid var(--stage-line);
  border-radius: 50%;
}

.filters__more {
  margin-top: var(--spacing-m);
  padding: 0;
  border: 0;
  background: none;
  color: var(--primary-color);
  font: inherit;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
}

.filters__more:hover {
  text-decoration: underline;
}
</style>
